'use client';

import { ThankYouElementDataType } from '@/components/sections/builder/elements/body/thankyou/type';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import { dummyCustomerOrderDetail } from '@/constant/dummyData/dummy-customer-order';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn, isEmpty, removeHTMLTags } from '@/lib/utils';
import { HttpTypes } from '@/types';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

export default function ElementContentThankyou({ element, data, preview }: ElementProps) {
  const content = element.data as ThankYouElementDataType;

  const order = preview
    ? dummyCustomerOrderDetail
    : (data as HttpTypes.StoreOrderSuccessPaymentResponse);

  React.useEffect(() => {
    if (preview && (!order || !order?.order_no)) return;

    SendEventAnalytic('purchase', {
      transaction_id: order?.order_no,
      value: order?.grand_total - Number(order?.platform_fee),
      tax: order?.total_tax,
      shipping: order?.shipping_cost,
      currency: 'IDR',
      items: order?.order_items.map((item) => ({
        id: item.item_id,
        name: item.product_name,
        variant: item.item_name,
        quantity: item.qty,
        price: item.sell_price,
      })),
    });

    SendEventMetapixel('Purchase', {
      content_ids: order?.order_no,
      content_type: 'purchase',
      contents: order?.order_items.map((item) => ({
        id: item.item_id,
        quantity: item.qty,
      })),
      num_items: order?.order_items.length,
      currency: 'IDR',
      value: order?.grand_total,
    });
  }, [order]);

  return (
    <section className='theme_section theme_thankyou_padding'>
      <div className='theme_layout_page_width mx-auto min-h-[calc(100vh_-_350px)] duration-100 md:container'>
        <div className='my-2 flex w-full flex-col items-center justify-center gap-4 p-10 pt-5'>
          {content.imageUrl && (
            <div
              className='relative aspect-square'
              style={{ width: `${content.imageWidth ?? 320}px` }}
            >
              <Image
                src={content.imageUrl}
                alt='Thankyou'
                fill
                className='object-cover object-center'
              />
            </div>
          )}
          <h2 className='text-2xl'>No Order: #{order?.order_no}</h2>
          <div
            className='text-lg'
            dangerouslySetInnerHTML={{ __html: content.labelThankyou || '' }}
          />
          {content.showHomeButton && (
            <Link href='/'>
              <Button
                className={cn('px-8 py-2', isEmpty(content.colorScheme) && 'theme_button')}
                style={
                  !isEmpty(content.colorScheme)
                    ? {
                        backgroundColor: content.colorScheme?.btnSolidBackgroud,
                        color: content.colorScheme?.btnSolidlabel,
                      }
                    : {}
                }
              >
                {removeHTMLTags(content.labelButton)}
              </Button>
            </Link>
          )}
        </div>
      </div>
      <style jsx>
        {`
          .theme_thankyou_padding {
            padding-top: ${content.padding?.top || 20}px;
            padding-bottom: ${content.padding?.bottom || 20}px;
            padding-left: ${content.padding?.left || 20}px;
            padding-right: ${content.padding?.right || 20}px;
          }
          .theme_section {
            background-color: ${content.colorScheme?.background};
            color: ${content.colorScheme?.text};
          }
          .theme_button_section {
            background-color: ${content.colorScheme?.btnSolidBackgroud};
            color: ${content.colorScheme?.btnSolidlabel};
          }
          .theme_button_section:hover {
            opacity: 0.8;
          }

          .image-width {
            width: ${content.imageWidth ?? 100}px;
          }
        `}
      </style>
    </section>
  );
}
